/**
 * Custom React Query hooks for data caching across the application
 * 
 * Cache Duration Strategy:
 * - User data: 10 min (rarely changes)
 * - Dashboard stats: 5 min (moderate updates)
 * - Learning paths/modules: 10 min (admin-controlled)
 * - Community posts: 2 min (user-generated, dynamic)
 * - Real-time data (chat): NO CACHING
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../services/api'

// ===========================================
// CACHE TIME CONSTANTS
// ===========================================

export const CACHE_TIMES = {
    // Long-lived data (10 minutes)
    USER_PROFILE: 10 * 60 * 1000,
    CAREER_PATHS: 10 * 60 * 1000,
    MODULES: 10 * 60 * 1000,

    // Moderate updates (5 minutes)
    DASHBOARD_STATS: 5 * 60 * 1000,
    QUIZZES: 5 * 60 * 1000,
    PROJECTS: 5 * 60 * 1000,

    // Dynamic content (2 minutes)
    COMMUNITY_POSTS: 2 * 60 * 1000,
    LEADERBOARD: 2 * 60 * 1000,

    // Time-sensitive (1 minute)
    NOTIFICATIONS: 1 * 60 * 1000,

    // Garbage collection (30 minutes default)
    GC_TIME: 30 * 60 * 1000,
}

// ===========================================
// QUERY KEYS - Centralized for consistency
// ===========================================

export const QUERY_KEYS = {
    // User
    currentUser: ['currentUser'],
    userProfile: (userId: string) => ['userProfile', userId],
    users: (filters?: any) => ['users', filters],
    followers: (userId: string) => ['followers', userId],
    following: (userId: string) => ['following', userId],

    // Dashboard
    publicStats: ['publicStats'],
    adminStats: ['adminStats'],
    instructorStats: ['instructorStats'],
    studentStats: ['studentStats'],

    // Learning
    careerPaths: (filters?: any) => ['careerPaths', filters],
    careerPath: (pathId: string) => ['careerPath', pathId],
    modules: (filters?: any) => ['modules', filters],
    module: (moduleId: string) => ['module', moduleId],
    quizzes: (filters?: any) => ['quizzes', filters],
    quiz: (quizId: string) => ['quiz', quizId],
    enrollments: ['enrollments'],
    progress: (pathId: string) => ['progress', pathId],

    // Community
    posts: (filters?: any) => ['posts', filters],
    post: (postId: string) => ['post', postId],
    comments: (postId: string) => ['comments', postId],

    // Projects
    projects: (filters?: any) => ['projects', filters],
    project: (projectId: string) => ['project', projectId],
    tasks: (projectId: string) => ['tasks', projectId],
    teams: ['teams'],

    // Leaderboard
    leaderboard: ['leaderboard'],

    // Achievements
    achievements: ['achievements'],
    certificates: ['certificates'],

    // Featured / Jobs
    featuredProjects: ['featuredProjects'],
    jobs: (params?: any) => ['jobs', params],
}

// ===========================================
// USER HOOKS
// ===========================================

export function useCurrentUser() {
    return useQuery({
        queryKey: QUERY_KEYS.currentUser,
        queryFn: async () => {
            const response = await api.get('/auth/profile/')
            return response.data
        },
        staleTime: CACHE_TIMES.USER_PROFILE,
        gcTime: CACHE_TIMES.GC_TIME,
    })
}

// ===========================================
// DASHBOARD HOOKS
// ===========================================

export function usePublicStats() {
    return useQuery({
        queryKey: QUERY_KEYS.publicStats,
        queryFn: async () => {
            const response = await api.get('/auth/public-stats/')
            return response.data
        },
        staleTime: CACHE_TIMES.DASHBOARD_STATS,
    })
}

// ===========================================
// LEARNING HOOKS
// ===========================================

export function useCareerPaths(filters?: { program?: string; difficulty?: string; search?: string }) {
    return useQuery({
        queryKey: QUERY_KEYS.careerPaths(filters),
        queryFn: async () => {
            const params = new URLSearchParams()
            if (filters?.program) params.append('program_type', filters.program)
            if (filters?.difficulty) params.append('difficulty_level', filters.difficulty)
            if (filters?.search) params.append('search', filters.search)

            // The student catalogue, and only the student catalogue, uses this.
            // It used to try /learning/admin/career-paths/ first and fall back on
            // 401 — which meant a student saw the ten published paths while an
            // admin or instructor saw thirteen, including retired and draft ones,
            // in the same course list. The admin dashboard has its own calls for
            // those.
            const response = await api.get(`/learning/career-paths/?${params}`)
            return response.data.results || response.data || []
        },
        staleTime: CACHE_TIMES.CAREER_PATHS,
    })
}

export function useEnrollments() {
    return useQuery({
        queryKey: QUERY_KEYS.enrollments,
        queryFn: async () => {
            const response = await api.get('/learning/enrollments/')
            return response.data.results || response.data || []
        },
        staleTime: CACHE_TIMES.DASHBOARD_STATS,
    })
}

// ===========================================
// PROJECT HOOKS
// ===========================================

export function useProjects(filters?: { status?: string; search?: string }) {
    return useQuery({
        queryKey: QUERY_KEYS.projects(filters),
        queryFn: async () => {
            const params = new URLSearchParams()
            if (filters?.status) params.append('status', filters.status)
            if (filters?.search) params.append('search', filters.search)
            const response = await api.get(`/projects/projects/?${params}`)
            return response.data.results || response.data || []
        },
        staleTime: CACHE_TIMES.PROJECTS,
    })
}

export function useTeams() {
    return useQuery({
        queryKey: QUERY_KEYS.teams,
        queryFn: async () => {
            const response = await api.get('/projects/teams/')
            return response.data.results || response.data || []
        },
        staleTime: CACHE_TIMES.PROJECTS,
    })
}

export function useAllTasks() {
    return useQuery({
        queryKey: ['allTasks'],
        queryFn: async () => {
            const response = await api.get('/projects/tasks/')
            return response.data.results || response.data || []
        },
        staleTime: CACHE_TIMES.PROJECTS,
    })
}

// ===========================================
// FEATURED PROJECTS
// ===========================================

export function useFeaturedProjects() {
    return useQuery({
        queryKey: QUERY_KEYS.featuredProjects,
        queryFn: async () => {
            const response = await api.get('/projects/projects/featured/')
            return (Array.isArray(response.data) ? response.data : []) as any[]
        },
        staleTime: CACHE_TIMES.PROJECTS,
    })
}

// ===========================================
// JOBS
// ===========================================

export function useJobs(params?: { page_size?: number }) {
    return useQuery({
        queryKey: QUERY_KEYS.jobs(params),
        queryFn: async () => {
            const p = new URLSearchParams()
            if (params?.page_size) p.append('page_size', String(params.page_size))
            const response = await api.get(`/learning/jobs/?${p}`)
            return (Array.isArray(response.data.results) ? response.data.results : []) as any[]
        },
        staleTime: 5 * 60 * 1000,
    })
}
